/**
 * The run page's live facts strip: Agent, Machine, Branch, Elapsed and Cost against budget, each a
 * tiny label over its value (the session drawer's fact grid). Elapsed ticks while the mission runs;
 * the cost cell edits the budget in place.
 */
import { CircleAlert, OctagonX } from '@orgus/ui/icons';
import { useState } from 'react';
import type { ReactNode } from 'react';
import type { AgentKind } from '@orgus/protocol';
import { Input, LiveNumber, MeterBar, Pressable, Switch, Tooltip, cn, formatCompact, formatDuration, formatMoney, useNow } from '@orgus/ui';
import { AgentKindLabel } from '../../adapters/AgentKind.tsx';
import { modelLabel } from '../../sessions/labels.ts';
import { parseBudget } from '../model.ts';

/** Five cells on a wide screen, two to a row on a phone, set apart by thin inset lines. */
const GRID = 'inset-dividers-sm grid min-w-0 grid-cols-2 gap-y-4 sm:grid-cols-5';
const CELL = 'flex min-w-0 flex-col gap-1 pr-4 even:pl-4 sm:pl-4 sm:first:pl-0';

/** An empty fact: a quiet dash, never a word in the value's place; the reason, if any, is the caption. */
function NoValue({ label }: { label: string }) {
  return (
    <span className="text-ui text-fg-subtle">
      <span aria-hidden>–</span>
      <span className="sr-only">{label}</span>
    </span>
  );
}

/** A name split into parts that may wrap: a break opportunity after each slash, dash and underscore. */
function breakable(text: string): ReactNode {
  const parts = text.split(/(?<=[/_-])/);
  return parts.map((p, i) => (
    <span key={i}>
      {p}
      {i < parts.length - 1 && <wbr />}
    </span>
  ));
}

function Fact({ label, children, sub, title, mono, wrap }: { label: string; children: ReactNode; sub?: ReactNode; title?: string; mono?: boolean; wrap?: boolean }) {
  return (
    <div className={CELL}>
      <dt className="truncate text-caption text-fg-subtle">{label}</dt>
      <dd className={cn('flex min-h-5 min-w-0 items-center text-fg', mono ? 'font-mono text-caption' : 'text-ui')} title={title}>
        {/* A phone wraps a long value (the branch) onto a second line rather than cut its telling end,
            breaking only after a slash, a dash or an underscore, never inside a word. */}
        <span className={cn('min-w-0 truncate', wrap && 'max-sm:whitespace-normal max-sm:break-normal')}>{wrap && typeof children === 'string' ? breakable(children) : children}</span>
      </dd>
      {sub != null && <dd className="flex h-4 min-w-0 items-center truncate text-caption text-fg-subtle num">{sub}</dd>}
    </div>
  );
}

function Elapsed({ from, to }: { from: number; to: number | null }) {
  const now = useNow(to == null ? 1_000 : 60_000);
  return <span className="num">{formatDuration(Math.max(0, (to ?? now) - from))}</span>;
}

function CostCell({ cost, tokens, budget, used, onBudget, hardStop, onHardStop, saving, error, settled }: { cost: number | null; tokens: number; budget: number | null; used: number | null; settled?: boolean; onBudget: (v: number | null) => Promise<boolean>; hardStop?: boolean; onHardStop?: (v: boolean) => Promise<boolean>; saving: boolean; error?: string }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [bad, setBad] = useState<string | undefined>();
  const start = () => {
    setDraft(budget != null ? String(budget) : '');
    setBad(undefined);
    setEditing(true);
  };
  const commit = async () => {
    const b = parseBudget(draft);
    if (b.error) return setBad(b.error);
    if (b.value === budget) return setEditing(false);
    if (await onBudget(b.value)) setEditing(false);
  };
  if (editing) {
    return (
      <div className={cn(CELL, 'gap-2')} data-cost-edit>
        <Input
          label="Budget in dollars"
          size="sm"
          inputMode="decimal"
          placeholder="25"
          autoFocus
          value={draft}
          disabled={saving}
          error={bad ?? error}
          onChange={(e) => setDraft(e.target.value)}
          // Moving to the hard stop switch keeps the editor open; leaving the cell saves.
          onBlur={(e) => {
            if (!e.currentTarget.closest('[data-cost-edit]')?.contains(e.relatedTarget as Node | null)) void commit();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void commit();
            if (e.key === 'Escape') setEditing(false);
          }}
        />
        {onHardStop && (
          <label className="flex min-w-0 items-center gap-2 text-caption text-fg-muted" data-budget-hard-stop>
            <Switch size="sm" checked={!!hardStop} disabled={saving} onCheckedChange={(v) => void onHardStop(v === true)} />
            <span className="truncate">Stop the mission at this budget</span>
          </label>
        )}
      </div>
    );
  }
  const over = used != null && used > 1;
  return (
    // A whole row on a phone, so the budget figure is never cut.
    <div className={cn(CELL, 'max-sm:col-span-2')}>
      <dt className="truncate text-caption text-fg-subtle">Cost</dt>
      <dd className="flex h-5 min-w-0 items-center gap-2 text-ui text-fg">
        {/* ADR 0078: tokens on a model with no price (Codex, Gemini) cost 0 here: show the tokens, never $0.00. */}
        {cost != null && cost > 0 ? <LiveNumber value={cost} format={formatMoney} /> : tokens > 0 ? <span className="num whitespace-nowrap" title={cost === 0 ? 'Not priced: Orgus has no price for this model' : undefined}>{formatCompact(tokens)} tokens</span> : <NoValue label={settled ? 'No cost' : 'No cost yet'} />}
        {budget != null && (
          <Tooltip content={hardStop ? 'Stops at this budget. Change the budget' : 'Change the budget'}>
            <Pressable className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-control text-caption text-fg-subtle hover:text-fg" onClick={start} aria-label="Change the budget">
              <span className="num">of {formatMoney(budget)}</span>
              {hardStop && <OctagonX className="size-3 shrink-0" aria-label="Stops at this budget" />}
            </Pressable>
          </Tooltip>
        )}
      </dd>
      {/* The meter only when there is something to measure: never an empty bar. */}
      <dd className="flex h-4 min-w-0 items-center">
        {over ? (
          <span className="inline-flex min-w-0 items-center gap-1 truncate text-caption font-medium text-status-danger">
            <CircleAlert className="size-3 shrink-0" aria-hidden />
            Over by {formatMoney((cost ?? 0) - (budget ?? 0))}
          </span>
        ) : used != null && used > 0 ? (
          <MeterBar value={used} tone={used >= 0.8 ? 'attention' : 'neutral'} label={`Budget used: ${Math.round(used * 100)} percent`} className="w-full" />
        ) : budget == null ? (
          // Without a budget the line under the cost is where one is set.
          <Pressable className="w-fit min-w-0 truncate rounded-control text-caption text-fg-subtle hover:text-fg" onClick={start}>
            Set a budget
          </Pressable>
        ) : cost == null && tokens > 0 ? (
          <span className="truncate text-caption text-fg-subtle">Not priced yet</span>
        ) : null}
      </dd>
    </div>
  );
}

/**
 * The Elapsed cell: where its clock stops and the line under it. QA-18: a run waiting on a person
 * stops its clock when the wait began and says so, rather than count on under Running.
 */
export function elapsedFact(span: { from: number; to: number | null } | null, spanLabel: string, waitingSince: number | null): { to: number | null; sub: string | undefined } | null {
  if (!span) return null;
  if (span.to != null) return { to: span.to, sub: spanLabel };
  if (waitingSince != null) return { to: Math.max(span.from, waitingSince), sub: 'Needs you, clock stopped' };
  return { to: null, sub: 'Running' };
}

export interface RunFactsProps {
  agentKind: AgentKind | null | undefined;
  model: string | null;
  machine: { name: string; online: boolean } | null;
  branch: string | null;
  /** The run has stopped: an unknown fact will stay unknown, so it reads None, not yet. */
  settled?: boolean;
  span: { from: number; to: number | null } | null;
  /** What a stopped clock means: Finished, Failed or Stopped. */
  spanLabel: string;
  /** The run waits on a person since then: the clock stops there. */
  waitingSince?: number | null;
  cost: number | null;
  tokens: number;
  budget: number | null;
  budgetUsed: number | null;
  onBudget: (v: number | null) => Promise<boolean>;
  /** QA-63: the mission stops when its spend passes the budget. */
  hardStop?: boolean;
  onHardStop?: (v: boolean) => Promise<boolean>;
  saving: boolean;
}

export function RunFacts({ agentKind, model, machine, branch, settled, span, spanLabel, cost, tokens, budget, budgetUsed, onBudget, hardStop, onHardStop, saving, waitingSince = null }: RunFactsProps) {
  const e = elapsedFact(span, spanLabel, waitingSince);
  return (
    <dl className={GRID} aria-label="Mission facts">
      <Fact label="Agent" sub={modelLabel(model)}>
        <AgentKindLabel kind={agentKind} />
      </Fact>
      <Fact label="Machine" title={machine?.name} sub={machine ? (machine.online ? 'Online' : 'Offline') : 'Not chosen yet'}>
        {machine?.name ?? <NoValue label="No machine" />}
      </Fact>
      <Fact label="Branch" title={branch ?? undefined} mono={!!branch} wrap sub={branch || settled ? undefined : 'Not known yet'}>
        {branch || <NoValue label="No branch" />}
      </Fact>
      <Fact label="Elapsed" sub={e?.sub}>
        {span && e && (e.to == null || e.to - span.from >= 1_000) ? <Elapsed from={span.from} to={e.to} /> : <NoValue label={span ? 'No time' : 'Not started'} />}
      </Fact>
      <CostCell settled={settled} cost={cost} tokens={tokens} budget={budget} used={budgetUsed} onBudget={onBudget} hardStop={hardStop} onHardStop={onHardStop} saving={saving} />
    </dl>
  );
}
